import { useCallback, useEffect, useState } from "@wordpress/element";

import { APIProvider } from "@vis.gl/react-google-maps";
import { Geolocator } from "../../../react/components/Geolocator.jsx";
import { PlaceAutocomplete } from "../../../react/components/PlaceAutocomplete.jsx";
import { StoresGoogleMap } from "./StoresGoogleMap.jsx";
import StoresList from "../../../react/components/StoresList.jsx";
import { __, _x } from "@wordpress/i18n";
import { useCookie } from "../../../react/services/cookie.js";
import { useSearchStores } from "../../../react/services/search.js";

export default function StoresMap({
  googleMapsApiKey,
  defaultLatLng: defaultLatLngString,
  defaultZoom,
  activeMarkerUrl,
  markerUrl,
  countries,
  defaultList,
}) {
  let defaultLatLng = null;
  if (defaultLatLngString) {
    const [lat, lng] = defaultLatLngString.split(",").map(Number);
    if (!isNaN(lat) && !isNaN(lng)) {
      defaultLatLng = { lat, lng };
    }
  }

  const [address, setAddress] = useState("");
  const [search, setSearch] = useState("");
  const [latLng, setLatLng] = useState(defaultLatLng);
  const [selectedStore, setSelectedStore] = useState(null);
  const [activeStore, setActiveStore] = useState(window.__ACTIVE_STORE__);
  const [, setActiveStoreId] = useCookie("active-store", "");

  const [{ data: storesFound, loading, error }, searchStores] =
    useSearchStores();

  const chooseStore = useCallback(
    function (store) {
      setActiveStore(store);
      setActiveStoreId(store.id);

      if (typeof window === "undefined") return;
      window.dispatchEvent(
        new CustomEvent("store_selected", { detail: store })
      );
    },
    [setActiveStore, setActiveStoreId, close]
  );

  const selectPlace = useCallback(function (place) {
    if (!place || (!place.location && !place.geometry)) {
      setSearch("");
      return;
    }

    const location = place.location || place.geometry.location;
    const lat = location.lat();
    const lng = location.lng();
    setLatLng({ lat, lng });
    setAddress(""); // Pour effacer l'ancien résultat mis en dur
    setSearch(`${lat},${lng}`);
  }, []);

  const selectGeolocation = useCallback(function (address) {
    if (!address) return;
    if (typeof address === "string") {
      // Cas particulier sans résultat
      setSearch(address);
      setAddress(address);
      return;
    }
    const lat = address.geometry.location.lat();
    const lng = address.geometry.location.lng();
    setLatLng({ lat, lng });
    setAddress(address.formatted_address);
    setSearch(`${lat},${lng}`);
  }, []);

  const selectStore = useCallback(function (store) {
    setSelectedStore(store);
    if (store) {
      setLatLng(store.latLng);
    }
  }, []);

  useEffect(
    function () {
      if (!search) return;
      searchStores(search, 300);
    },
    [search, searchStores]
  );

  return (
    <APIProvider apiKey={googleMapsApiKey}>
      <div className="stores-search">
        <span className="stores-search-geolocation">
          <Geolocator
            className="stores-search-geolocation-trigger"
            triggerOnLoad
            onChange={selectGeolocation}
            title={_x(
              "Me localiser",
              "Frontend stores locator",
              "manitoba-stores"
            )}
          />
        </span>
        <span className="stores-search-input">
          <PlaceAutocomplete
            options={{
              componentRestrictions: countries
                ? {
                    country: countries
                      .split(",")
                      .map((country) => country.trim().toLowerCase()),
                  }
                : {},
            }}
            onChange={selectPlace}
            value={address}
            placeholder={_x(
              "Saisissez une ville ou un code postal",
              "Frontend stores locator",
              "manitoba-stores"
            )}
          />
        </span>
      </div>
      {!!error && (
        <div className="stores-search-error">
          <p>{error}</p>
        </div>
      )}
      {!!loading && (
        <div className="stores-search-loading">
          <p>
            {_x("Chargement...", "Frontend stores locator", "manitoba-stores")}
          </p>
        </div>
      )}
      <div class="stores-map-results">
        <div class="stores-map-results-map">
          <StoresGoogleMap
            center={latLng}
            zoom={defaultZoom ? parseInt(defaultZoom, 10) : 10}
            markerUrl={markerUrl}
            activeMarkerUrl={activeMarkerUrl}
            list={storesFound || defaultList}
            selected={selectedStore}
            onSelect={selectStore}
          />
        </div>
        <div class="stores-map-results-list">
          <StoresList
            list={storesFound || defaultList}
            activeId={activeStore ? activeStore.id : null}
            selected={selectedStore}
            onSelect={selectStore}
            onChoose={chooseStore}
          />
        </div>
      </div>
    </APIProvider>
  );
}
